"use client";

import { useState, useEffect } from "react";
import { usePathname } from "next/navigation";
import Logo from "./Logo";
import styles from "./Navbar.module.css";
import { ChevronDown, Menu, X } from "lucide-react";
import Link from "next/link";

export default function Navbar() {
  const pathname = usePathname();
  const isHome = pathname === "/";

  const getLinkHref = (hash: string) => {
    return isHome ? hash : `/${hash}`;
  };

  const isActive = (path: string) => {
    return pathname === path || pathname.startsWith(`${path}/`);
  };
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [resourcesOpen, setResourcesOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 20) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const toggleMenu = () => setIsOpen(!isOpen);

  return (
    <header className={`${styles.header} ${scrolled ? styles.scrolled : ""} ${isOpen ? styles.headerOpen : ""}`}>
      <div className={`${styles.container} container`}>
        <div className={styles.logoArea}>
          <Logo light={false} />
        </div>

        {/* Desktop Navigation */}
        <nav className={styles.nav}>
          <ul className={styles.navList}>
            <li className={styles.navItem}>
              <Link href="/features" className={`${styles.navLink} ${isActive('/features') ? styles.active : ''}`}>Features</Link>
            </li>
            <li className={styles.navItem}>
              <Link href="/modules" className={`${styles.navLink} ${isActive('/modules') ? styles.active : ''}`}>Modules</Link>
            </li>
            <li className={styles.navItem}>
              <Link href="/pricing" className={`${styles.navLink} ${isActive('/pricing') ? styles.active : ''}`}>Pricing</Link>
            </li>
            <li className={styles.navItem}>
              <Link href="/blog" className={`${styles.navLink} ${isActive('/blog') ? styles.active : ''}`}>Blog</Link>
            </li>
            <li className={styles.navItem}>
              <Link href="/contact" className={`${styles.navLink} ${isActive('/contact') ? styles.active : ''}`}>Contact</Link>
            </li>
          </ul>
        </nav>

        {/* Action Buttons */}
        <div className={styles.actions}>
          <a href={getLinkHref("#login")} className={styles.btnLogin}>Login</a>
          <a href={getLinkHref("#demo")} className={styles.btnDemo}>Book a Demo</a>
        </div>

        {/* Mobile Toggle */}
        <button 
          className={styles.mobileToggle} 
          onClick={toggleMenu}
          aria-label={isOpen ? "Close menu" : "Open menu"}
          id="navbar-mobile-toggle"
        >
          {isOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Drawer */}
      <div className={`${styles.mobileMenu} ${isOpen ? styles.mobileMenuOpen : ""}`}>
        <ul className={styles.mobileNavList}>
          <li>
            <Link href="/features" className={`${styles.mobileNavLink} ${isActive('/features') ? styles.active : ''}`} onClick={toggleMenu}>Features</Link>
          </li>
          <li>
            <Link href="/modules" className={`${styles.mobileNavLink} ${isActive('/modules') ? styles.active : ''}`} onClick={toggleMenu}>Modules</Link>
          </li>
          <li>
            <Link href="/pricing" className={`${styles.mobileNavLink} ${isActive('/pricing') ? styles.active : ''}`} onClick={toggleMenu}>Pricing</Link>
          </li>
          <li>
            <Link href="/blog" className={`${styles.mobileNavLink} ${isActive('/blog') ? styles.active : ''}`} onClick={toggleMenu}>Blog</Link>
          </li>
          <li>
            <Link href="/contact" className={`${styles.mobileNavLink} ${isActive('/contact') ? styles.active : ''}`} onClick={toggleMenu}>Contact</Link>
          </li>
          <li className={styles.mobileActions}>
            <a href={getLinkHref("#login")} className={styles.mobileBtnLogin} onClick={toggleMenu}>Login</a>
            <a href={getLinkHref("#demo")} className={styles.mobileBtnDemo} onClick={toggleMenu}>Book a Demo</a>
          </li>
        </ul>
      </div>
    </header>
  );
}
